import React from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  Target,
  AlertTriangle,
  Trophy,
  Activity,
  Eye,
  TrendingDown,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import BettingProgressionCard from '../../components/client/BettingProgressionCard';
import RiskProjectionCard from '../../components/client/RiskProjectionCard';
import RecentActivityCard from '../../components/client/RecentActivityCard';
import EventStatusCard from '../../components/client/EventStatusCard';

const ClientOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: dashboardData, isLoading, error } = useQuery(
    ['client-dashboard', user?.id],
    () => api.get(`/dashboard/client/${user?.id}`).then(res => res.data),
    {
      enabled: !!user?.id,
      refetchInterval: 30000,
      staleTime: 10000
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar dashboard
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error.response?.data?.error || 'Error de conexión'}
        </p>
      </div>
    );
  }

  const { client, activeEvent, recentTransactions, personalStats } = dashboardData;
  const { financialInfo } = client;

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  // Usar ganancia neta del backend o calcular si no existe
  const netProfit = financialInfo.netProfit !== undefined
    ? financialInfo.netProfit
    : (financialInfo.currentBalance || 0) - (financialInfo.initialCapital || 0);

  const profitPercentage = financialInfo.profitPercentage !== undefined
    ? financialInfo.profitPercentage
    : (financialInfo.initialCapital > 0 ? ((netProfit / financialInfo.initialCapital) * 100) : 0);

  const statsCards = [
    {
      title: 'Capital Inicial',
      value: formatCurrency(financialInfo.initialCapital),
      icon: DollarSign,
      color: 'primary',
      description: 'Inversión base'
    },
    {
      title: 'Saldo Actual',
      value: formatCurrency(financialInfo.currentBalance),
      icon: Activity,
      color: financialInfo.currentBalance >= financialInfo.initialCapital ? 'success' : 'warning',
      description: 'Disponible ahora'
    },
    {
      title: 'Ganancias Netas',
      value: formatCurrency(financialInfo.netProfit || netProfit),
      icon: (financialInfo.netProfit || netProfit) >= 0 ? TrendingUp : TrendingDown,
      color: (financialInfo.netProfit || netProfit) > 0 ? 'success' : (financialInfo.netProfit || netProfit) < 0 ? 'warning' : 'gray',
      trend: (financialInfo.netProfit || netProfit) !== 0 ? `${(financialInfo.netProfit || netProfit) > 0 ? '+' : ''}${profitPercentage.toFixed(1)}%` : '0%',
      description: (financialInfo.netProfit || netProfit) >= 0 ? 'Ganancia real' : 'Pérdida actual'
    },
    {
      title: 'ROI Actual',
      value: `${profitPercentage.toFixed(1)}%`,
      icon: Trophy,
      color: profitPercentage > 0 ? 'success' : profitPercentage < 0 ? 'warning' : 'gray',
      description: 'Rendimiento sobre inversión'
    }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-primary rounded-2xl p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">
              ¡Hola {client.fullName.split(' ')[0]}! 👋
            </h1>
            <p className="text-primary-100">
              {activeEvent?.isParticipating
                ? `Participando en: ${activeEvent.name}`
                : 'No estás participando en ningún evento activo'
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold mb-1">
              {formatCurrency(financialInfo.currentBalance)}
            </p>
            <p className="text-primary-100 text-sm">
              Saldo disponible
            </p>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-xl ${
                stat.color === 'primary' ? 'bg-primary-100 dark:bg-primary-900/30' :
                stat.color === 'success' ? 'bg-success-100 dark:bg-success-900/30' :
                stat.color === 'warning' ? 'bg-warning-100 dark:bg-warning-900/30' :
                'bg-gray-100 dark:bg-gray-700'
              }`}>
                <stat.icon className={`w-6 h-6 ${
                  stat.color === 'primary' ? 'text-primary-600 dark:text-primary-400' :
                  stat.color === 'success' ? 'text-success-600 dark:text-success-400' :
                  stat.color === 'warning' ? 'text-warning-600 dark:text-warning-400' :
                  'text-gray-600 dark:text-gray-400'
                }`} />
              </div>

              {stat.trend && (
                <div className={`flex items-center space-x-1 text-sm ${
                  stat.trend.startsWith('+') ? 'text-success-600 dark:text-success-400' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  <TrendingUp className="w-4 h-4" />
                  <span className="font-medium">{stat.trend}</span>
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                {stat.title}
              </h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">
                {stat.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {stat.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            data-event-status
          >
            <EventStatusCard activeEvent={activeEvent} />
          </motion.div>

          {activeEvent?.isParticipating && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <BettingProgressionCard
                progression={activeEvent.progression}
                currentStep={activeEvent.currentStep}
                eventSeedAmount={activeEvent.eventSeedAmount}
              />
            </motion.div>
          )}
        </div>

        <div className="space-y-6">
          {activeEvent?.isParticipating && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <RiskProjectionCard
                riskProjection={activeEvent.riskProjection}
                currentBalance={financialInfo.currentBalance}
                eventSeedAmount={activeEvent.eventSeedAmount}
              />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              📊 Mis Estadísticas
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-success-50 dark:bg-success-900/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Trophy className="w-5 h-5 text-success-600 dark:text-success-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Empates Ganados
                  </span>
                </div>
                <span className="text-lg font-bold text-success-600 dark:text-success-400">
                  {personalStats.total_wins}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Target className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Apuestas Realizadas
                  </span>
                </div>
                <span className="text-lg font-bold text-warning-600 dark:text-warning-400">
                  {personalStats.total_bets}
                </span>
              </div>

              <div className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Activity className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Tasa de Éxito
                  </span>
                </div>
                <span className="text-lg font-bold text-primary-600 dark:text-primary-400">
                  {personalStats.total_bets > 0
                    ? ((personalStats.total_wins / personalStats.total_bets) * 100).toFixed(1)
                    : 0
                  }%
                </span>
              </div>

              <div className="pt-3 border-t border-gray-200 dark:border-dark-700">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Volumen Total Apostado</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {formatCurrency(personalStats.total_bet_amount)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600 dark:text-gray-400">Ganancias Brutas</span>
                  <span className="font-medium text-success-600 dark:text-success-400">
                    {formatCurrency(personalStats.total_win_amount)}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <RecentActivityCard transactions={recentTransactions} />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="card p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          🚀 Acciones Rápidas
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/client/history')}
            className="btn-secondary flex items-center justify-center space-x-2 hover:bg-primary-100 dark:hover:bg-primary-900/30"
          >
            <Eye className="w-4 h-4" />
            <span>Ver Historial Completo</span>
          </button>

          <button
            onClick={() => navigate('/client/profile')}
            className="btn-secondary flex items-center justify-center space-x-2 hover:bg-primary-100 dark:hover:bg-primary-900/30"
          >
            <TrendingUp className="w-4 h-4" />
            <span>Mi Perfil</span>
          </button>

          <button
            onClick={() => {
              const eventSection = document.querySelector('[data-event-status]');
              if (eventSection) {
                eventSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="btn-secondary flex items-center justify-center space-x-2 hover:bg-primary-100 dark:hover:bg-primary-900/30"
          >
            <CheckCircle className="w-4 h-4" />
            <span>Estado del Evento</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ClientOverview;